import { cva, type VariantProps } from 'class-variance-authority';
import React, { forwardRef, type ComponentPropsWithoutRef } from 'react';
import { Link as ReactLink } from 'react-router-dom';
import { cn } from '../../../../utils/styles';
import MenuIcon from '../../../../Guidance/components/Layout/Menu/MenuIcon';

const stylesActiveMark = [
  'before:absolute',
  'before:top-0',
  'before:h-[5px]',
  'before:w-full',
  'before:bg-white',
  'before:rounded-b-[5px]',
  'before:transition before:ease-in-out before:duration-150',
];

const linkVariants = cva(
  'text-base cursor-pointer text-primary font-medium transition-colors hover:text-primary flex items-center',
  {
    variants: {
      variant: {
        base: 'text-primary-text hover:text-addition-2',
        muted: 'text-muted underline hover:text-muted-foreground',
        header: ['relative text-white hover:text-white', 'whitespace-nowrap'],
        guidance: 'text-[18px] text-addition1-80 hover:text-addition1',
      },
      /** Use active only with variant 'header'! */
      active: {
        // true: stylesActiveMark,
        true: '',
        false: '',
      },
    },
    defaultVariants: {
      variant: 'base',
      active: false,
    },
  },
);

interface ExtendedLinkProps extends ComponentPropsWithoutRef<typeof ReactLink>, VariantProps<typeof linkVariants> {
  icon?: React.ReactNode;
}

const Link = forwardRef<HTMLAnchorElement, ExtendedLinkProps>(
  ({ className, variant, active, children, icon, ...restProps }: ExtendedLinkProps, ref) => (
    <ReactLink
      ref={ref}
      className={cn(
        linkVariants({ variant, active, className }),
        !active &&
          variant === 'header' && [
            'text-muted',
            /** Styles for 'slide-down' animation on hover: */
            ...stylesActiveMark,
            'before:top-[-5px]',
            'hover:before:-translate-y-[-5px]',
          ],
        active && variant === 'guidance' && 'font-bold text-primary-text',
      )}
      {...restProps}
    >
      <span
        className={cn(['flex', 'items-center', 'gap-1', '[&_svg_path]:fill-current'], {
          'gap-[10px]': variant === 'guidance',
        })}
      >
        {variant === 'guidance' ? <MenuIcon icon={icon} active={active} /> : icon}
        {children}
      </span>
    </ReactLink>
  ),
);

Link.displayName = 'Link';

Link.defaultProps = {
  icon: undefined,
};

export default Link;
